import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, FileSpreadsheet } from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { cn } from '@/nucleo/utilitarios';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import { fmtDec, fmtInt, formatarMesLongo, totaisRegime } from './calculos';
import { DADOS_INDICADORES_MENSAIS } from './dados';
import { exportarIndicadoresMensaisParaXlsx } from './exportar';
import { IndicadoresMensaisCharts } from './IndicadoresMensaisCharts';

/**
 * `/operacao/financeiro/indicadores-mensais` — retrato mensal do transporte
 * escolar por DRE e regime de contratação (PF × PJ): pagamentos, frota,
 * crianças atendidas e médias operacionais, na mesma leitura da planilha de
 * indicadores hoje mantida à mão.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. Lido de `dados.ts` (que monta as
 * datas relativas a hoje): troque `DADOS_INDICADORES_MENSAIS` e o
 * `EstadoSimulado` pela leitura da API, no mesmo `dados.ts` (padrão de
 * `modulos/admin/integracoes/dados.ts`) quando o módulo nascer na API.
 *
 * O filtro de competência é decorativo por enquanto — muda o rótulo do
 * bloco 1 e o nome do arquivo exportado, mas não refiltra a fixture (só há
 * uma competência mockada). Mesmo padrão de `painel-executivo/PainelExecutivoPage.tsx`.
 * A exportação em XLSX (`exportarIndicadoresMensaisParaXlsx`) roda inteira no
 * navegador — não depende da API, mesma lógica de `lib/indicadoresMensaisExport.ts`
 * do protótipo.
 */

const FIXTURE = DADOS_INDICADORES_MENSAIS;

function CelulaCabecalho({
  children,
  className,
  colSpan,
  rowSpan,
}: {
  children: ReactNode;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
}) {
  return (
    <th
      scope="col"
      colSpan={colSpan}
      rowSpan={rowSpan}
      className={cn(
        'px-2 py-2 text-xs font-semibold text-foreground/80 uppercase tracking-wider whitespace-nowrap bg-muted/40 border-b border-border',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Celula({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <td
      className={cn(
        'px-2 py-1.5 text-sm border-b border-border/50 whitespace-nowrap tabular-nums',
        className,
      )}
    >
      {children}
    </td>
  );
}

function CelulaDinheiro({ valor, className }: { valor: number; className?: string }) {
  return (
    <Celula className={className}>
      <MoneyDisplay value={valor} />
    </Celula>
  );
}

export function IndicadoresMensaisPage() {
  const [mes, setMes] = useState(FIXTURE.mesReferencia.slice(0, 7));
  const cenario = useCenarioDeTela();

  const linhasBloco1 = useMemo(
    () =>
      FIXTURE.porDre.map((r) => ({
        ...r,
        pf: totaisRegime(r.pf, true),
        pj: totaisRegime(r.pj, false),
      })),
    [],
  );

  const t1 = useMemo(
    () =>
      linhasBloco1.reduce(
        (acc, r) => {
          acc.bPf += r.pf.bruto;
          acc.ncPf += r.pf.criancasNc;
          acc.cdPf += r.pf.criancasCad;
          acc.kmPf += r.pf.kmAdic;
          acc.inss += r.pf.inss;
          acc.bcInssPf += r.pf.brutoComInss;
          acc.bPj += r.pj.bruto;
          acc.ncPj += r.pj.criancasNc;
          acc.cdPj += r.pj.criancasCad;
          acc.kmPj += r.pj.kmAdic;
          return acc;
        },
        {
          bPf: 0,
          ncPf: 0,
          cdPf: 0,
          kmPf: 0,
          inss: 0,
          bcInssPf: 0,
          bPj: 0,
          ncPj: 0,
          cdPj: 0,
          kmPj: 0,
        },
      ),
    [linhasBloco1],
  );

  const t2 = useMemo(
    () =>
      linhasBloco1.reduce(
        (acc, r) => {
          const bruto = r.pf.bruto + r.pj.bruto;
          const nc = r.pf.criancasNc + r.pj.criancasNc;
          const cd = r.pf.criancasCad + r.pj.criancasCad;
          const km = r.pf.kmTotal + r.pj.kmTotal;
          acc.bruto += bruto;
          acc.nc += nc;
          acc.cd += cd;
          acc.km += km;
          acc.inss += r.pf.inss;
          acc.bcInss += bruto + r.pf.inss;
          acc.cnvPf += r.pf.veiculosConv;
          acc.cnvPj += r.pj.veiculosConv;
          acc.acsPf += r.pf.veiculosAcess;
          acc.acsPj += r.pj.veiculosAcess;
          acc.crcPf += r.pf.veiculosCreche;
          acc.crcPj += r.pj.veiculosCreche;
          return acc;
        },
        {
          bruto: 0,
          nc: 0,
          cd: 0,
          km: 0,
          inss: 0,
          bcInss: 0,
          cnvPf: 0,
          cnvPj: 0,
          acsPf: 0,
          acsPj: 0,
          crcPf: 0,
          crcPj: 0,
        },
      ),
    [linhasBloco1],
  );

  const semDados = cenario === 'vazio';

  const handleExportar = () => {
    exportarIndicadoresMensaisParaXlsx(FIXTURE, mes);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Indicadores Mensais"
        description="Visão consolidada do mês por DRE, regime (PF/PJ) e modalidade — pagamentos, frota, alunos atendidos e médias operacionais em um só lugar."
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="gap-1">
            <Link to="/operacao/financeiro/painel-executivo">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Painel Executivo
            </Link>
          </Button>
          <MonthFilter label="Competência" value={mes} onChange={setMes} hideLabel />
          <Button
            variant="default"
            size="sm"
            className="gap-2"
            disabled={cenario !== 'sucesso'}
            onClick={handleExportar}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Exportar XLSX
          </Button>
        </div>
      </PageHeader>

      <EstadoSimulado cenario={cenario} descricao="os indicadores mensais">
        {semDados ? (
          <EmptyState
            title="Nenhum indicador para o período"
            description="Não há apuração lançada para a competência selecionada. Ajuste a competência ou volte quando o mês fechar."
          />
        ) : (
          <div className="space-y-6">
            {/* Storytelling visual — 3 atos (tendência, composição PF×PJ, eficiência) */}
            <IndicadoresMensaisCharts
              dados={FIXTURE}
              serie={FIXTURE.serieMensal}
              carregandoMes={false}
              carregandoSerie={false}
            />

            {/* Bloco 1 — Pagamentos PF × PJ */}
            <Card className="rounded-2xl overflow-hidden">
              <CardHeader className="pb-3 bg-primary/15">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden="true" />
                  Pagamentos por regime — {formatarMesLongo(mes)}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Valor bruto, crianças (NC/CAD) e KM adicional por DRE, separados em PF e PJ. INSS
                  (4%) incide apenas sobre o bruto PF.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm border-collapse"
                    aria-label="Pagamentos por regime, PF e PJ, por DRE"
                  >
                    <thead>
                      <tr>
                        <CelulaCabecalho className="sticky left-0 z-20 bg-muted/40 text-center border-r-2 border-border">
                          DRE
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/30">
                          Bruto PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">NC</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">CAD</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Total</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">KM adic.</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">INSS</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Bruto c/ INSS</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/30">
                          Bruto PJ
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">NC</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">CAD</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Total</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">KM adic.</CelulaCabecalho>
                      </tr>
                    </thead>
                    <tbody>
                      {linhasBloco1.map((r) => (
                        <tr key={r.dreCodigo} className="hover:bg-muted/30">
                          <Celula className="sticky left-0 z-10 bg-muted/60 font-semibold text-center border-r-2 border-border">
                            {r.dreCodigo}
                          </Celula>
                          <CelulaDinheiro
                            valor={r.pf.bruto}
                            className="text-right border-l-2 border-primary/20 bg-primary/4.5 text-foreground font-semibold"
                          />
                          <Celula className="text-right">{fmtInt(r.pf.criancasNc)}</Celula>
                          <Celula className="text-right">{fmtInt(r.pf.criancasCad)}</Celula>
                          <Celula className="text-right font-medium">
                            {fmtInt(r.pf.criancasTotal)}
                          </Celula>
                          <Celula className="text-right">{fmtDec(r.pf.kmAdic, 2)}</Celula>
                          <CelulaDinheiro
                            valor={r.pf.inss}
                            className="text-right bg-primary/4.5 text-foreground font-semibold"
                          />
                          <CelulaDinheiro
                            valor={r.pf.brutoComInss}
                            className="text-right bg-primary/4.5 text-foreground font-semibold"
                          />
                          <CelulaDinheiro
                            valor={r.pj.bruto}
                            className="text-right border-l-2 border-primary/20 bg-primary/4.5 text-foreground font-semibold"
                          />
                          <Celula className="text-right">{fmtInt(r.pj.criancasNc)}</Celula>
                          <Celula className="text-right">{fmtInt(r.pj.criancasCad)}</Celula>
                          <Celula className="text-right font-medium">
                            {fmtInt(r.pj.criancasTotal)}
                          </Celula>
                          <Celula className="text-right">{fmtDec(r.pj.kmAdic, 2)}</Celula>
                        </tr>
                      ))}
                      <tr className="bg-primary/5 font-bold">
                        <Celula className="sticky left-0 z-10 bg-primary/10 font-semibold text-center border-r-2 border-border">
                          TOTAL
                        </Celula>
                        <CelulaDinheiro
                          valor={t1.bPf}
                          className="text-right border-l-2 border-primary/30"
                        />
                        <Celula className="text-right">{fmtInt(t1.ncPf)}</Celula>
                        <Celula className="text-right">{fmtInt(t1.cdPf)}</Celula>
                        <Celula className="text-right">{fmtInt(t1.ncPf + t1.cdPf)}</Celula>
                        <Celula className="text-right">{fmtDec(t1.kmPf, 2)}</Celula>
                        <CelulaDinheiro valor={t1.inss} className="text-right" />
                        <CelulaDinheiro valor={t1.bcInssPf} className="text-right" />
                        <CelulaDinheiro
                          valor={t1.bPj}
                          className="text-right border-l-2 border-primary/30"
                        />
                        <Celula className="text-right">{fmtInt(t1.ncPj)}</Celula>
                        <Celula className="text-right">{fmtInt(t1.cdPj)}</Celula>
                        <Celula className="text-right">{fmtInt(t1.ncPj + t1.cdPj)}</Celula>
                        <Celula className="text-right">{fmtDec(t1.kmPj, 2)}</Celula>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Bloco 2 — Consolidado + Frota */}
            <Card className="rounded-2xl overflow-hidden">
              <CardHeader className="pb-3 bg-primary/15">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden="true" />
                  Consolidado geral + Frota por modalidade
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Totais PF+PJ por DRE e composição da frota ativa (Convencionais · Acessíveis ·
                  Creche), separando PF e PJ.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm border-collapse"
                    aria-label="Consolidado geral e frota por modalidade, por DRE"
                  >
                    <thead>
                      <tr>
                        <CelulaCabecalho
                          rowSpan={2}
                          className="sticky left-0 z-20 bg-muted/40 text-center border-r-2 border-border"
                        >
                          DRE
                        </CelulaCabecalho>
                        <CelulaCabecalho rowSpan={2} className="text-right">
                          Bruto
                        </CelulaCabecalho>
                        <CelulaCabecalho colSpan={3} className="text-center">
                          Crianças
                        </CelulaCabecalho>
                        <CelulaCabecalho rowSpan={2} className="text-right">
                          KM total
                        </CelulaCabecalho>
                        <CelulaCabecalho rowSpan={2} className="text-right">
                          INSS
                        </CelulaCabecalho>
                        <CelulaCabecalho rowSpan={2} className="text-right">
                          Bruto c/ INSS
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={2}
                          className="text-center border-l-2 border-primary/30"
                        >
                          Convencionais
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={2}
                          className="text-center border-l-2 border-primary/30"
                        >
                          Acessíveis
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={2}
                          className="text-center border-l-2 border-primary/30"
                        >
                          Creche
                        </CelulaCabecalho>
                      </tr>
                      <tr>
                        <CelulaCabecalho className="text-right">NC</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">CAD</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Total</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                      </tr>
                    </thead>
                    <tbody>
                      {linhasBloco1.map((r) => {
                        const bruto = r.pf.bruto + r.pj.bruto;
                        const nc = r.pf.criancasNc + r.pj.criancasNc;
                        const cd = r.pf.criancasCad + r.pj.criancasCad;
                        const km = r.pf.kmTotal + r.pj.kmTotal;
                        return (
                          <tr key={r.dreCodigo} className="hover:bg-muted/30">
                            <Celula className="sticky left-0 z-10 bg-muted/60 font-semibold text-center border-r-2 border-border">
                              {r.dreCodigo}
                            </Celula>
                            <CelulaDinheiro
                              valor={bruto}
                              className="text-right bg-primary/4.5 text-foreground font-semibold"
                            />
                            <Celula className="text-right">{fmtInt(nc)}</Celula>
                            <Celula className="text-right">{fmtInt(cd)}</Celula>
                            <Celula className="text-right font-medium">{fmtInt(nc + cd)}</Celula>
                            <Celula className="text-right">{fmtDec(km, 2)}</Celula>
                            <CelulaDinheiro
                              valor={r.pf.inss}
                              className="text-right bg-primary/4.5 text-foreground font-semibold"
                            />
                            <CelulaDinheiro
                              valor={bruto + r.pf.inss}
                              className="text-right bg-primary/4.5 text-foreground font-semibold"
                            />
                            <Celula className="text-right border-l-2 border-primary/20">
                              {fmtInt(r.pf.veiculosConv)}
                            </Celula>
                            <Celula className="text-right">{fmtInt(r.pj.veiculosConv)}</Celula>
                            <Celula className="text-right border-l-2 border-primary/20">
                              {fmtInt(r.pf.veiculosAcess)}
                            </Celula>
                            <Celula className="text-right">{fmtInt(r.pj.veiculosAcess)}</Celula>
                            <Celula className="text-right border-l-2 border-primary/20">
                              {fmtInt(r.pf.veiculosCreche)}
                            </Celula>
                            <Celula className="text-right">{fmtInt(r.pj.veiculosCreche)}</Celula>
                          </tr>
                        );
                      })}
                      <tr className="bg-primary/5 font-bold">
                        <Celula className="sticky left-0 z-10 bg-primary/10 font-semibold text-center border-r-2 border-border">
                          TOTAL
                        </Celula>
                        <CelulaDinheiro valor={t2.bruto} className="text-right" />
                        <Celula className="text-right">{fmtInt(t2.nc)}</Celula>
                        <Celula className="text-right">{fmtInt(t2.cd)}</Celula>
                        <Celula className="text-right">{fmtInt(t2.nc + t2.cd)}</Celula>
                        <Celula className="text-right">{fmtDec(t2.km, 2)}</Celula>
                        <CelulaDinheiro valor={t2.inss} className="text-right" />
                        <CelulaDinheiro valor={t2.bcInss} className="text-right" />
                        <Celula className="text-right border-l-2 border-primary/20">
                          {fmtInt(t2.cnvPf)}
                        </Celula>
                        <Celula className="text-right">{fmtInt(t2.cnvPj)}</Celula>
                        <Celula className="text-right border-l-2 border-primary/20">
                          {fmtInt(t2.acsPf)}
                        </Celula>
                        <Celula className="text-right">{fmtInt(t2.acsPj)}</Celula>
                        <Celula className="text-right border-l-2 border-primary/20">
                          {fmtInt(t2.crcPf)}
                        </Celula>
                        <Celula className="text-right">{fmtInt(t2.crcPj)}</Celula>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Bloco 3 — Médias e razões */}
            <Card className="rounded-2xl overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden="true" />
                  Médias e razões operacionais
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Valor criança/dia, remuneração média por condutor, crianças por veículo e km
                  mensal por condutor — calculados a partir dos blocos 1 e 2.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm border-collapse"
                    aria-label="Médias e razões operacionais, por DRE"
                  >
                    <thead>
                      <tr>
                        <CelulaCabecalho
                          rowSpan={2}
                          className="sticky left-0 z-20 bg-muted/40 text-center border-r-2 border-border"
                        >
                          DRE
                        </CelulaCabecalho>
                        <CelulaCabecalho rowSpan={2} className="text-right">
                          Criança / dia (R$)
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={3}
                          className="text-center border-l-2 border-primary/30"
                        >
                          Condutores remunerados
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={3}
                          className="text-center border-l-2 border-primary/30"
                        >
                          Remuneração média / condutor
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={3}
                          className="text-center border-l-2 border-primary/30"
                        >
                          Crianças / veículo
                        </CelulaCabecalho>
                        <CelulaCabecalho
                          colSpan={3}
                          className="text-center border-l-2 border-primary/30"
                        >
                          KM mensal / condutor
                        </CelulaCabecalho>
                      </tr>
                      <tr>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Geral</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Média</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Média</CelulaCabecalho>
                        <CelulaCabecalho className="text-right border-l-2 border-primary/20">
                          PF
                        </CelulaCabecalho>
                        <CelulaCabecalho className="text-right">PJ</CelulaCabecalho>
                        <CelulaCabecalho className="text-right">Média</CelulaCabecalho>
                      </tr>
                    </thead>
                    <tbody>
                      {linhasBloco1.map((r) => {
                        const brutoTotal = r.pf.bruto + r.pj.bruto;
                        const criancasTotal = r.pf.criancasTotal + r.pj.criancasTotal;
                        const vPf = r.pf.veiculosTotal;
                        const vPj = r.pj.veiculosTotal;
                        const cPf = r.pf.condutores;
                        const cPj = r.pj.condutores;
                        const cGer = cPf + cPj;
                        const dias = r.diasLetivos || 22;
                        const valorCDia =
                          criancasTotal > 0 ? brutoTotal / (criancasTotal * dias) : 0;
                        const remPf = cPf > 0 ? r.pf.bruto / cPf : 0;
                        const remPj = cPj > 0 ? r.pj.bruto / cPj : 0;
                        const remMed = cGer > 0 ? brutoTotal / cGer : 0;
                        const cvPf = vPf > 0 ? r.pf.criancasTotal / vPf : 0;
                        const cvPj = vPj > 0 ? r.pj.criancasTotal / vPj : 0;
                        const cvMed = vPf + vPj > 0 ? criancasTotal / (vPf + vPj) : 0;
                        const kmPf = cPf > 0 ? r.pf.kmTotal / cPf : 0;
                        const kmPj = cPj > 0 ? r.pj.kmTotal / cPj : 0;
                        const kmMed = cGer > 0 ? (r.pf.kmTotal + r.pj.kmTotal) / cGer : 0;
                        return (
                          <tr key={r.dreCodigo} className="hover:bg-muted/30">
                            <Celula className="sticky left-0 z-10 bg-muted/60 font-semibold text-center border-r-2 border-border">
                              {r.dreCodigo}
                            </Celula>
                            <CelulaDinheiro
                              valor={valorCDia}
                              className="text-right bg-primary/4.5 text-foreground font-semibold"
                            />
                            <Celula className="text-right border-l-2 border-primary/20">
                              {fmtInt(cPf)}
                            </Celula>
                            <Celula className="text-right">{fmtInt(cPj)}</Celula>
                            <Celula className="text-right bg-primary/4.5 text-foreground font-semibold">
                              {fmtInt(cGer)}
                            </Celula>
                            <CelulaDinheiro
                              valor={remPf}
                              className="text-right border-l-2 border-primary/20"
                            />
                            <CelulaDinheiro valor={remPj} className="text-right" />
                            <CelulaDinheiro
                              valor={remMed}
                              className="text-right bg-primary/4.5 text-foreground font-semibold"
                            />
                            <Celula className="text-right border-l-2 border-primary/20">
                              {fmtDec(cvPf, 1)}
                            </Celula>
                            <Celula className="text-right">{fmtDec(cvPj, 1)}</Celula>
                            <Celula className="text-right bg-primary/4.5 text-foreground font-semibold">
                              {fmtDec(cvMed, 1)}
                            </Celula>
                            <Celula className="text-right border-l-2 border-primary/20">
                              {fmtDec(kmPf, 2)}
                            </Celula>
                            <Celula className="text-right">{fmtDec(kmPj, 2)}</Celula>
                            <Celula className="text-right bg-primary/4.5 text-foreground font-semibold">
                              {fmtDec(kmMed, 2)}
                            </Celula>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Legenda */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                  Legenda
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3 text-sm">
                  {FIXTURE.porDre.map((r) => (
                    <div key={r.dreCodigo} className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary w-12">{r.dreCodigo}</span>
                      <span className="text-muted-foreground">{r.dreNome}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  PF = Pessoa Física · PJ = Pessoa Jurídica (inclui cooperativas) · NC =
                  não-cadeirantes · CAD = cadeirantes · INSS = alíquota patronal de 4% sobre o bruto
                  PF (regra TEG-SP).
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </EstadoSimulado>
    </div>
  );
}

export default IndicadoresMensaisPage;
